/**
 * Zod schemas for the label_raster extension (in-place zarr label mask edits).
 */
import { z } from "zod"

import { labelRasterManifestSourceSchema } from "./manifest.ts"

const classIdSchema = z.number().int().min(0).max(65535)

/**
 * Default "unannotated" label id. Matches `virdx_attrs.misc.unknown_id` of the
 * production domain-representation masks (fine28 taxonomy).
 */
export const DEFAULT_UNANNOTATED_ID = 255

/** Default brush radius in base image pixels. */
export const DEFAULT_BRUSH_RADIUS_PX = 24

/**
 * Editing knobs shared by the template schema and per-project overrides. All
 * optional here; the template schema adds defaults, the override schema does
 * not (so a saved override never freezes today's defaults into the project).
 */
const labelRasterEditFields = {
  /**
   * Classes offered in the picker. Default: every id in the zarr's region
   * palette. Background (0) is always offered regardless of this list: true
   * slide background is a real class, distinct from "unannotated".
   */
  editable_class_ids: z.array(classIdSchema).optional(),
  /**
   * Label id the eraser writes. Distinct from background (0) so downstream
   * training can tell "annotator removed this label" from true slide
   * background. Renders transparent; the brush may always repaint it.
   */
  unannotated_id: classIdSchema.optional(),
  /** Values the brush may paint over. Default `[0, unannotated_id]`. */
  brush_overwritable_ids: z.array(classIdSchema).optional(),
  default_brush_radius_px: z.number().int().min(1).max(4096).optional(),
  /** Completion precheck: require at least one changed pixel. */
  require_edit: z.boolean().optional()
}

export const labelRasterConfigSchema = z.object({
  type: z.literal("label_raster"),
  /** Must equal the `domain` metadata of the overlay series to edit (e.g. "tissue_segmentation"). */
  domain: z.string().min(1),
  editable_class_ids: labelRasterEditFields.editable_class_ids,
  unannotated_id: classIdSchema.default(DEFAULT_UNANNOTATED_ID),
  brush_overwritable_ids: labelRasterEditFields.brush_overwritable_ids,
  default_brush_radius_px: z.number().int().min(1).max(4096).default(DEFAULT_BRUSH_RADIUS_PX),
  require_edit: z.boolean().default(false)
})

export type LabelRasterConfig = z.infer<typeof labelRasterConfigSchema>

/** Per-project overrides stored in project_modules.config (all optional, no defaults baked in). */
export const labelRasterProjectConfigSchema = z.strictObject({
  domain: z.string().min(1).optional(),
  ...labelRasterEditFields
})

export type LabelRasterProjectConfig = z.infer<typeof labelRasterProjectConfigSchema>

/** Effective eraser value + brush overwritable set. */
export interface LabelRasterEditRules {
  /** Value the eraser writes ("unannotated"). */
  unannotatedId: number
  /** Values the brush may paint over. */
  overwritableIds: number[]
}

type LabelRasterEditRuleSource = Partial<Pick<LabelRasterProjectConfig, "unannotated_id" | "brush_overwritable_ids">>

/**
 * Resolve the editing rules from a template schema and (optional) per-project
 * overrides; overrides win field by field. When `brush_overwritable_ids` is not
 * set explicitly the brush may paint over background and the unannotated id.
 */
export function resolveLabelRasterEditRules(
  schema: LabelRasterEditRuleSource | null | undefined,
  overrides?: LabelRasterEditRuleSource | null
): LabelRasterEditRules {
  const unannotatedId = overrides?.unannotated_id ?? schema?.unannotated_id ?? DEFAULT_UNANNOTATED_ID
  const explicit = overrides?.brush_overwritable_ids ?? schema?.brush_overwritable_ids
  const overwritableIds = explicit ? [...explicit] : unannotatedId === 0 ? [0] : [0, unannotatedId]
  return { unannotatedId, overwritableIds }
}

/** Background class id: always listed and selectable in the label raster picker. */
export const BACKGROUND_CLASS_ID = 0

/**
 * Whether a class may be selected (listed in the panel, picked in the viewer)
 * for editing. The unannotated id is what the eraser writes, never a class to
 * paint; background is always selectable; `editableClassIds` (null = all)
 * restricts the remaining palette classes.
 */
export function isLabelRasterClassSelectable(
  value: number,
  rules: { unannotatedId: number; editableClassIds?: readonly number[] | null }
): boolean {
  if (value === rules.unannotatedId) return false
  if (value === BACKGROUND_CLASS_ID) return true
  return !rules.editableClassIds || rules.editableClassIds.includes(value)
}

const manifestChunkJsonSchema = z.object({
  index: z.tuple([z.number().int().min(0), z.number().int().min(0)]),
  originalCrc32: z.number().int().min(0).max(0xffffffff),
  runCount: z.number().int().min(0),
  changedPixels: z.number().int().min(0),
  bbox: z.tuple([z.number().int().min(0), z.number().int().min(0), z.number().int().min(0), z.number().int().min(0)]),
  /** base64 of little-endian int32 triples */
  runs: z.string()
})

export const labelRasterManifestJsonSchema = z.object({
  version: z.literal(1),
  source: labelRasterManifestSourceSchema,
  chunks: z.array(manifestChunkJsonSchema),
  classesUsed: z.array(classIdSchema),
  editedAt: z.string().min(1),
  editor: z.object({ app: z.literal("vxannotate"), version: z.string() })
})

export const createLabelRasterSchema = z.object({
  project_module_id: z.string().min(1),
  /** The overlay (label zarr) series being edited. */
  series_id: z.string().min(1),
  domain: z.string().min(1),
  base_series_id: z.string().min(1).optional(),
  manifest: labelRasterManifestJsonSchema
})

export const updateLabelRasterSchema = z.object({
  domain: z.string().min(1).optional(),
  base_series_id: z.string().min(1).nullable().optional(),
  manifest: labelRasterManifestJsonSchema.optional()
})
